import { useEffect, useState } from 'react';
import { Bot, CheckCircle2, Cloud, KeyRound, PlugZap, RefreshCw, Save, ServerCog } from 'lucide-react';
import { toast } from 'sonner';

import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';


const IbiConfiguration = () => {
  const [configuration, setConfiguration] = useState({ provider: 'openai', model: 'gpt-6-luna', base_url: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [keyConfigured, setKeyConfigured] = useState(false);
  const [openaiDefaultModel, setOpenaiDefaultModel] = useState('gpt-6-luna');
  const [source, setSource] = useState('environment');
  const [ollamaModels, setOllamaModels] = useState([]);
  const [loadingOllamaModels, setLoadingOllamaModels] = useState(false);
  const [openaiModels, setOpenaiModels] = useState([]);
  const [loadingOpenaiModels, setLoadingOpenaiModels] = useState(false);
  const [openaiSearch, setOpenaiSearch] = useState('');

  const loadOllamaModels = async (showFeedback = false) => {
    setLoadingOllamaModels(true);
    try {
      const response = await api.get('/admin/ai/ollama/models');
      setOllamaModels(response.data.models || []);
      setConfiguration((current) => ({ ...current, base_url: response.data.base_url || current.base_url }));
      if (showFeedback) toast.success(`${response.data.models?.length || 0} modelos detectados en Ollama`);
      return response.data.models || [];
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo consultar el catálogo de Ollama');
      return [];
    } finally {
      setLoadingOllamaModels(false);
    }
  };

  const loadOpenaiModels = async (showFeedback = false) => {
    setLoadingOpenaiModels(true);
    try {
      const response = await api.get('/admin/ai/openai/models');
      const models = response.data.models || [];
      setOpenaiModels(models);
      if (showFeedback) toast.success(`${models.length} modelos detectados para OpenAI`);
      return models;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo consultar el catálogo de OpenAI');
      return [];
    } finally {
      setLoadingOpenaiModels(false);
    }
  };

  const loadConfiguration = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/ai');
      const provider = response.data.provider || 'openai';
      setConfiguration({
        provider,
        model: response.data.model || 'gpt-6-luna',
        base_url: response.data.base_url || response.data.ollama_base_url || '',
      });
      setKeyConfigured(Boolean(response.data.openai_key_configured));
      setOpenaiDefaultModel(response.data.openai_default_model || 'gpt-6-luna');
      setSource(response.data.source || 'environment');
      if (provider === 'ollama') await loadOllamaModels();
      else await loadOpenaiModels();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo cargar la configuración de Ibi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  const selectProvider = async (provider) => {
    if (provider === 'openai') {
      setConfiguration((current) => ({ ...current, provider, model: openaiDefaultModel, base_url: '' }));
      if (openaiModels.length === 0) await loadOpenaiModels();
      return;
    }
    setConfiguration((current) => ({ ...current, provider }));
    if (provider === 'ollama' && ollamaModels.length === 0) {
      const models = await loadOllamaModels();
      const toolModel = models.find((model) => model.capabilities?.includes('tools'));
      if (toolModel) setConfiguration((current) => ({ ...current, provider, model: toolModel.name }));
    }
  };

  const saveConfiguration = async (event) => {
    event.preventDefault();
    const model = configuration.model.trim();
    if (!model) {
      toast.error('Indicá el modelo que debe usar Ibi');
      return;
    }

    setSaving(true);
    try {
      const response = await api.put('/admin/ai', {
        provider: configuration.provider,
        model,
        base_url: configuration.provider === 'ollama' ? configuration.base_url : null,
      });
      setConfiguration({
        provider: response.data.provider,
        model: response.data.model,
        base_url: response.data.base_url || configuration.base_url,
      });
      setSource(response.data.source);
      toast.success('Configuración de Ibi actualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const response = await api.post('/admin/ai/test', {
        provider: configuration.provider,
        model: configuration.model,
        base_url: configuration.provider === 'ollama' ? configuration.base_url : null,
      });
      toast.success(response.data.message || 'Conexión verificada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo verificar la conexión');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  const usingOllama = configuration.provider === 'ollama';
  const compatibleOllamaModels = ollamaModels.filter((model) => model.capabilities?.includes('tools'));
  const filteredOpenaiModels = openaiModels.filter((model) => model.id.toLowerCase().includes(openaiSearch.trim().toLowerCase()));
  const selectedOpenaiModelIsVisible = filteredOpenaiModels.some((model) => model.id === configuration.model);

  return (
    <Layout>
      <div className="max-w-3xl space-y-6">
        <div className="flex items-center gap-3">
          <Bot className="h-8 w-8 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="page-title">Ibi · Modelo de IA</h1>
            <p className="page-subtitle">Elegí el proveedor y modelo que responde las consultas de inventario.</p>
          </div>
        </div>

        <form onSubmit={saveConfiguration} className="space-y-6">
          <Card className="card-subtle">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-start gap-3">
                <ServerCog className="mt-0.5 h-5 w-5 text-primary" strokeWidth={1.5} />
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Proveedor</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Las consultas, permisos y validaciones de Ibi son iguales para ambos proveedores.</p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <button
                  type="button"
                  onClick={() => selectProvider('openai')}
                  className={`rounded-lg border p-4 text-left transition-colors ${!usingOllama ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                >
                  <div className="flex items-center gap-2 font-medium"><Cloud className="h-4 w-4 text-primary" strokeWidth={1.5} /> OpenAI</div>
                  <p className="mt-1 text-xs text-muted-foreground">Modelo pago mediante la clave protegida del backend.</p>
                </button>
                <button
                  type="button"
                  onClick={() => selectProvider('ollama')}
                  className={`rounded-lg border p-4 text-left transition-colors ${usingOllama ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                >
                  <div className="flex items-center gap-2 font-medium"><ServerCog className="h-4 w-4 text-primary" strokeWidth={1.5} /> Ollama</div>
                  <p className="mt-1 text-xs text-muted-foreground">Modelo libre ejecutado en la infraestructura de I4.</p>
                </button>
              </div>
            </CardContent>
          </Card>

          <Card className="card-subtle">
            <CardContent className="p-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="ibi-model">Modelo</Label>
                <Input
                  id="ibi-model"
                  value={configuration.model}
                  onChange={(event) => setConfiguration((current) => ({ ...current, model: event.target.value }))}
                  placeholder={usingOllama ? 'gpt-oss:20b' : 'gpt-6-luna'}
                  maxLength={120}
                  autoComplete="off"
                />
                <p className="text-xs text-muted-foreground">
                  {usingOllama ? 'Ibi prioriza modelos que informan capacidad de uso de herramientas.' : `Al seleccionar OpenAI se propone el modelo configurado en el backend: ${openaiDefaultModel}.`}
                </p>
              </div>

              {!usingOllama && (
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium"><Cloud className="h-4 w-4 text-primary" strokeWidth={1.5} /> Modelos disponibles de OpenAI</div>
                      <p className="mt-1 text-xs text-muted-foreground">Se consultan con la clave del backend; la clave nunca llega al navegador.</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => loadOpenaiModels(true)} disabled={loadingOpenaiModels}>
                      <RefreshCw className={`mr-2 h-4 w-4 ${loadingOpenaiModels ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                      Actualizar modelos
                    </Button>
                  </div>

                  <Input
                    value={openaiSearch}
                    onChange={(event) => setOpenaiSearch(event.target.value)}
                    placeholder="Filtrar modelos, por ejemplo: gpt-6"
                    autoComplete="off"
                  />

                  {openaiModels.length > 0 ? (
                    <Select value={configuration.model} onValueChange={(model) => setConfiguration((current) => ({ ...current, model }))}>
                      <SelectTrigger aria-label="Elegir modelo de OpenAI">
                        <SelectValue placeholder="Elegí un modelo de OpenAI" />
                      </SelectTrigger>
                      <SelectContent>
                        {!selectedOpenaiModelIsVisible && configuration.model && (
                          <SelectItem value={configuration.model}>{configuration.model} · modelo configurado</SelectItem>
                        )}
                        {filteredOpenaiModels.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            {model.id}{model.owned_by ? ` · ${model.owned_by}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-xs text-amber-600 dark:text-amber-400">No se pudieron obtener modelos. Podés indicar uno manualmente y verificarlo antes de guardar.</p>
                  )}
                  {openaiModels.length > 0 && filteredOpenaiModels.length === 0 && (
                    <p className="text-xs text-muted-foreground">No hay modelos que coincidan con el filtro.</p>
                  )}
                  <p className="text-xs text-muted-foreground">Ibi valida el acceso al modelo seleccionado al probar la conexión y antes de guardar la configuración.</p>
                </div>
              )}

              {usingOllama && (
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium"><PlugZap className="h-4 w-4 text-primary" strokeWidth={1.5} /> Ollama disponible</div>
                      <p className="mt-1 break-all text-xs text-muted-foreground">{configuration.base_url}</p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={() => loadOllamaModels(true)} disabled={loadingOllamaModels}>
                      <RefreshCw className={`mr-2 h-4 w-4 ${loadingOllamaModels ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                      Actualizar modelos
                    </Button>
                  </div>
                  {compatibleOllamaModels.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {compatibleOllamaModels.map((model) => (
                        <Button
                          key={model.name}
                          type="button"
                          variant={configuration.model === model.name ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setConfiguration((current) => ({ ...current, model: model.name }))}
                        >
                          {model.name}{model.parameter_size ? ` · ${model.parameter_size}` : ''}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-amber-600 dark:text-amber-400">No se detectaron modelos con capacidad de herramientas.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="card-subtle">
            <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                {usingOllama ? <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" strokeWidth={1.5} /> : <KeyRound className={`mt-0.5 h-5 w-5 ${keyConfigured ? 'text-primary' : 'text-amber-500'}`} strokeWidth={1.5} />}
                <div>
                  <p className="text-sm font-medium">{usingOllama ? 'Ollama se conecta sólo desde el backend' : `Clave de OpenAI ${keyConfigured ? 'configurada' : 'pendiente'}`}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{usingOllama ? 'La URL está autorizada por el entorno del backend; no se admite una dirección arbitraria desde la interfaz.' : 'La clave se administra sólo en el entorno del backend y nunca se muestra ni guarda desde esta pantalla.'}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="text-xs text-muted-foreground">{source === 'database' ? 'Selección guardada' : 'Valor del entorno'}</span>
                <Button type="button" variant="outline" onClick={testConnection} disabled={testing}>
                  <PlugZap className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  {testing ? 'Probando...' : 'Probar conexión'}
                </Button>
                <Button type="submit" disabled={saving}>
                  <Save className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </Layout>
  );
};

export default IbiConfiguration;
